import React, { useMemo, useState, useEffect } from "react";
import { CheckboxAnswer, Familjen29Task, Familjen29TaskAnswer } from "data-models/task-models";
import { QUESTION_ACTIONS, useFollowUp } from "providers/FollowUpProvider";
import { useFullscreen } from "providers/FullscreenProvider";
import { Tab, Flex, Tabs, VStack, TabList, TabPanel, TabPanels, Box, Text, HStack } from "@chakra-ui/react";
import { TaskAdvice } from "./Common";
import { apiBase } from "services/config";
import { MultiTaskHeading, TaskHeading } from "./Common/TaskHeading";
import CheckboxAnswerGroup from "common/CheckboxAnswerGroup/CheckboxAnswerGroup";
import { useTranslation } from "react-i18next";
import {
  centeredBorderTopFlex,
  fadedSpan,
  fullCenteredVStackMinH,
  fullColumnAlignCenteredFlex,
  fullColumnFlex,
  fullTabPanel,
  fullTabPanels,
  fullVStackProps,
  letterBox,
  letterHStackProps,
  letterTile,
  styledTab,
  tabListSpacing,
} from "styles/common.styles";

type Part = keyof Familjen29TaskAnswer;

export default function Familjen29Task_Cmp() {
  const { isFullscreen } = useFullscreen();

  const { currentTask, dispatch } = useFollowUp();
  const task = currentTask as Familjen29Task;
  const parts: Part[] = ["a", "b", "c", "d", "e", "f"];

  const currentTaskPart = useMemo<Part | null>(() => {
    const s = currentTask.step;
    if (s % 2 === 0) return null;
    const idx = Math.floor((s - 1) / 2);
    return parts[idx] ?? null;
  }, [currentTask.step]);
  const emptyAnswerOptions: Familjen29TaskAnswer = {
    a: [],
    b: [],
    c: [],
    d: [],
    e: [],
    f: [],
  };
  const opts = task.answerOptions ?? emptyAnswerOptions;

  const onAnswerChange = (answers: CheckboxAnswer[]) => {
    if (!currentTaskPart) return;
    const payload: Familjen29TaskAnswer = {
      ...opts,
      [currentTaskPart]: answers,
    };
    dispatch({ type: QUESTION_ACTIONS.UPDATE_ANSWERS, payload });
    dispatch({ type: QUESTION_ACTIONS.UNSKIP_CURRENT_TASK });
  };

  const onChangeTab = (index: number) => {
    dispatch({ type: QUESTION_ACTIONS.SET_CURRENT_STEP, payload: index });
  };

  const { t } = useTranslation();

  const LetterTile: React.FC<{ letter: string }> = ({ letter }) => (
    <Box {...letterBox}>
      <Text {...letterTile}>{letter}</Text>
    </Box>
  );

  const LettersRow: React.FC<{ letters: string[] }> = ({ letters }) => (
    <HStack {...letterHStackProps}>
      {letters.map(letter => (
        <LetterTile key={letter} letter={letter} />
      ))}
    </HStack>
  );
  return (
    <Flex {...fullColumnFlex}>
      <VStack {...fullVStackProps}>
        <TaskHeading description={task.description} subtitle={task.subtitle} headingTitle={task.title} />

        <Tabs w="full" h="full" pos="relative" variant="unstyled" onChange={onChangeTab} index={currentTask.step}>
          <TabList {...tabListSpacing}>
            <Flex {...centeredBorderTopFlex} />
            <Tab {...styledTab}>
              <span style={fadedSpan}>{t("Step")} 1</span>
              {t("Explain_Scenario_Title")}
            </Tab>
            <Tab {...styledTab}>
              <span style={fadedSpan}>{t("Step")} 2</span>
              {t("Mark_All_Options_Text")}
            </Tab>
          </TabList>

          <TabPanels {...fullTabPanels}>
            <TabPanel {...fullTabPanel}>
              <Flex {...fullColumnFlex}>
                <Flex {...fullColumnAlignCenteredFlex}>
                  <LettersRow letters={["a/ɑ", "e", "i", "o", "u"]} />
                </Flex>

                <TaskAdvice instructions={task.taskInstructions} marginTop="auto !important" />
              </Flex>
            </TabPanel>

            <TabPanel {...fullTabPanels}>
              <Flex {...fullColumnFlex}>
                <VStack {...fullCenteredVStackMinH}>
                  <CheckboxAnswerGroup options={opts.a} onChange={onAnswerChange} />
                  <TaskAdvice instructions={task.answerInstructions} marginTop="auto !important" />
                </VStack>
              </Flex>
            </TabPanel>

            <TabPanel {...fullTabPanel}>
              <Flex {...fullColumnFlex}>
                <Flex {...fullColumnAlignCenteredFlex}>
                  <LettersRow letters={["y", "m", "n", "s", "l"]} />
                </Flex>

                <TaskAdvice instructions={task.taskInstructions} marginTop="auto !important" />
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanels}>
              <Flex {...fullColumnFlex}>
                <VStack {...fullCenteredVStackMinH}>
                  <CheckboxAnswerGroup options={opts.b} onChange={onAnswerChange} />
                  <TaskAdvice instructions={task.answerInstructions} marginTop="auto !important" />
                </VStack>
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanel}>
              <Flex {...fullColumnFlex}>
                <Flex {...fullColumnAlignCenteredFlex}>
                  <LettersRow letters={["r", "t", "p", "k", "f"]} />
                </Flex>

                <TaskAdvice instructions={task.taskInstructions} marginTop="auto !important" />
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanels}>
              <Flex {...fullColumnFlex}>
                <VStack {...fullCenteredVStackMinH}>
                  <CheckboxAnswerGroup options={opts.c} onChange={onAnswerChange} />
                  <TaskAdvice instructions={task.answerInstructions} marginTop="auto !important" />
                </VStack>
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanel}>
              <Flex {...fullColumnFlex}>
                <Flex {...fullColumnAlignCenteredFlex}>
                  <LettersRow letters={["h", "b", "d", "g/ɡ", "c"]} />
                </Flex>

                <TaskAdvice instructions={task.taskInstructions} marginTop="auto !important" />
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanels}>
              <Flex {...fullColumnFlex}>
                <VStack {...fullCenteredVStackMinH}>
                  <CheckboxAnswerGroup options={opts.d} onChange={onAnswerChange} />
                  <TaskAdvice instructions={task.answerInstructions} marginTop="auto !important" />
                </VStack>
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanel}>
              <Flex {...fullColumnFlex}>
                <Flex {...fullColumnAlignCenteredFlex}>
                  <LettersRow letters={["j", "q", "v", "w", "x"]} />
                </Flex>

                <TaskAdvice instructions={task.taskInstructions} marginTop="auto !important" />
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanels}>
              <Flex {...fullColumnFlex}>
                <VStack {...fullCenteredVStackMinH}>
                  <CheckboxAnswerGroup options={opts.e} onChange={onAnswerChange} />
                  <TaskAdvice instructions={task.answerInstructions} marginTop="auto !important" />
                </VStack>
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanel}>
              <Flex {...fullColumnFlex}>
                <Flex {...fullColumnAlignCenteredFlex}>
                  <LettersRow letters={["z", "å", "ä", "ö"]} />
                </Flex>

                <TaskAdvice instructions={task.taskInstructions} marginTop="auto !important" />
              </Flex>
            </TabPanel>
            <TabPanel {...fullTabPanels}>
              <Flex {...fullColumnFlex}>
                <VStack {...fullCenteredVStackMinH}>
                  <CheckboxAnswerGroup options={opts.f} onChange={onAnswerChange} />
                  <TaskAdvice instructions={task.answerInstructions} marginTop="auto !important" />
                </VStack>
              </Flex>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Flex>
  );
}
